import React from "react"
import Cabecalho from "../Cabecalho"
import useAppStore from "../../stores/AppStore"
import { RoboConfig } from "../../services/apiRobo"
import { Atendimentos } from "./ext/Atendimentos"
import { Participantes } from "./ext/Participantes"
import { Cidades } from "./ext/Cidades"
import { Kanban } from "./ext/Kanban"
import { Stats } from "./ext/Stats"
import { Agenda } from "./ext/Agenda"
import { Enquetes } from "./ext/Enquetes"
import { Contatos } from "./ext/Contatos"
import { Logs } from "./ext/Logs"
import { Config } from "./ext/Config"
import "./robo-ext.css"

/**
 * Console do Robô Suporte & Comercial — porte FIEL da interface da extensão (sidepanel),
 * reusando o CSS original escopado em .robo-ext. Esqueleto: topbar + barra de status com os
 * pills dos robôs + rail lateral de ícones + área principal que troca a visão pela rail ativa.
 * Etapa 1: shell + Atendimentos (lista unificada + conversa). Demais rails em porte.
 */

type Rail =
    | "chats"
    | "participants"
    | "cities"
    | "kanban"
    | "stats"
    | "appointments"
    | "polls"
    | "contacts"
    | "logs"
    | "config"

const RAILS_TOPO: Array<{ id: Rail; icon: string; title: string }> = [
    { id: "chats", icon: "💬", title: "Chats" },
    { id: "participants", icon: "👥", title: "Participantes" },
    { id: "cities", icon: "🌎", title: "Cidades" },
    { id: "kanban", icon: "📊", title: "Funil Kanban" },
    { id: "stats", icon: "📈", title: "Estatísticas" },
]
const RAILS_SEC: Array<{ id: Rail; icon: string; title: string }> = [
    { id: "appointments", icon: "📅", title: "Agenda de apresentações" },
    { id: "polls", icon: "☑️", title: "Enquetes" },
    { id: "contacts", icon: "📞", title: "Contatos" },
    { id: "logs", icon: "📜", title: "Logs" },
]

export function Index() {
    const perfil = useAppStore(s => s.configuracoes?.user?.perfil)
    const [rail, setRail] = React.useState<Rail>("chats")
    const [status, setStatus] = React.useState<any>(null)
    const shellRef = React.useRef<HTMLDivElement>(null)

    React.useEffect(() => {
        RoboConfig.status()
            .then(setStatus)
            .catch(() => setStatus(null))
    }, [])

    // O painel tem um Drawer fixo à esquerda e um AppBar fixo no topo (MUI). Posiciona o shell
    // do robô EXATAMENTE na área de conteúdo (à direita da sidebar, abaixo do AppBar), medindo
    // em runtime, para o rail e a topbar não ficarem atrás da chrome do painel.
    React.useLayoutEffect(() => {
        const shell = shellRef.current
        if (!shell) return
        function posicionar() {
            if (!shell) return
            const drawer = document.querySelector('[class*="MuiDrawer-paper"]') as HTMLElement | null
            const appbar = document.querySelector('[class*="MuiAppBar-root"]') as HTMLElement | null
            const left = drawer ? Math.round(drawer.getBoundingClientRect().right) : 80
            const top = appbar ? Math.round(appbar.getBoundingClientRect().bottom) : 100
            shell.style.position = "fixed"
            shell.style.left = left + "px"
            shell.style.top = top + "px"
            shell.style.right = "0"
            shell.style.bottom = "0"
            shell.style.height = "auto"
            shell.style.width = "auto"
            shell.style.zIndex = "1"
        }
        posicionar()
        const ro = new ResizeObserver(posicionar)
        try {
            ro.observe(document.body)
        } catch (_) {}
        window.addEventListener("resize", posicionar)
        const t1 = window.setTimeout(posicionar, 300)
        const t2 = window.setTimeout(posicionar, 1200)
        return () => {
            ro.disconnect()
            window.removeEventListener("resize", posicionar)
            window.clearTimeout(t1)
            window.clearTimeout(t2)
        }
    }, [])

    if (perfil !== "CLIENTEFIEL") {
        return (
            <>
                <Cabecalho pagina="Robô Suporte & Comercial" />
                <p style={{ padding: 32, textAlign: "center", color: "#667781" }}>
                    Esta área é exclusiva da equipe Cliente Fiel.
                </p>
            </>
        )
    }

    return (
        <>
            <Cabecalho pagina="Robô Suporte & Comercial" />
            <div className="robo-ext">
                <div className="robo-ext-shell" ref={shellRef}>
                    <header className="topbar">
                        <div className="brand">
                            <span className="logo-dot"></span>
                            <strong>Cliente Fiel</strong>
                            <span className="muted">Robô Suporte &amp; Comercial</span>
                        </div>
                        <RobotPills status={status} />
                        <button
                            className={"icon-btn" + (rail === "config" ? " active" : "")}
                            title="Configurações"
                            onClick={() => setRail("config")}
                            style={{ marginLeft: 8 }}
                        >
                            ⚙
                        </button>
                    </header>

                    <div className="app-shell">
                        <nav className="side-rail" aria-label="Categorias">
                            {RAILS_TOPO.map(r => (
                                <button
                                    key={r.id}
                                    className={"rail-btn" + (rail === r.id ? " active" : "")}
                                    title={r.title}
                                    onClick={() => setRail(r.id)}
                                >
                                    <span className="rail-icon">{r.icon}</span>
                                </button>
                            ))}
                            <div className="rail-divider"></div>
                            {RAILS_SEC.map(r => (
                                <button
                                    key={r.id}
                                    className={"rail-btn" + (rail === r.id ? " active" : "")}
                                    title={r.title}
                                    onClick={() => setRail(r.id)}
                                >
                                    <span className="rail-icon">{r.icon}</span>
                                </button>
                            ))}
                        </nav>

                        <main className="main-area">
                            {rail === "chats" && <Atendimentos />}
                            {rail === "participants" && <Participantes />}
                            {rail === "cities" && <Cidades />}
                            {rail === "kanban" && <Kanban />}
                            {rail === "stats" && <Stats />}
                            {rail === "appointments" && <Agenda />}
                            {rail === "polls" && <Enquetes />}
                            {rail === "contacts" && <Contatos />}
                            {rail === "logs" && <Logs />}
                            {rail === "config" && <Config />}
                        </main>
                    </div>
                </div>
            </div>
        </>
    )
}

/** Pills dos robôs (Vendedor / Suporte / Comunidade IA) — visual fiel; tempo vem do status quando houver. */
function RobotPills({ status }: { status: any }) {
    const pills = [
        { icon: "💼", title: "Vendedor IA", t: status?.vendedorUltimoEm },
        { icon: "🎧", title: "Suporte IA", t: status?.suporteUltimoEm },
        { icon: "🧑‍🤝‍🧑", title: "Comunidade IA", t: status?.comunidadeUltimoEm },
    ]
    return (
        <div className="robot-activity-pills" style={{ display: "flex" }}>
            {pills.map(p => (
                <div className="robot-pill-group" key={p.title}>
                    <span className="robot-pill" title={p.title}>
                        <span className="robot-pill-icon">{p.icon}</span>
                        <span className="robot-pill-time">{p.t ? fmtHora(p.t) : "—"}</span>
                    </span>
                </div>
            ))}
        </div>
    )
}

function fmtHora(v: any): string {
    try {
        return new Date(v).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
    } catch {
        return "—"
    }
}

export default Index
